import { getMonth, format, getYear, isValid, parse, parseJSON, isMatch, addDays } from 'date-fns';

/**
 * @typedef {{after?: string, before?: string, future?: boolean, past?: boolean, weekends?: boolean, weekdays?: boolean, only?: string[], except?: string[]}} DisabledDates
 * @typedef {{after: boolean, before: boolean, future: boolean, past: boolean, weekend: boolean, weekday: boolean, value: (string|string), dateNumber: (number|string)}} DateObject
 * @typedef {{ value: (string) }} ValueObject
 */

var MIDDLE_ENDIAN_FORMAT = 'MM/dd/yyyy';
var getTodayDate = function getTodayDate() {
  return format(new Date(), MIDDLE_ENDIAN_FORMAT);
};
var isFiscalDateFormat = function isFiscalDateFormat(dateString) {
  return isMatch(dateString, 'yyyyMMdd');
};

/**
 * checks to see if string is in Middle Endian format (MM/DD/YYYY)
 * @param {string} dateString
 * @returns {boolean}
 */
var isMiddleEndianFormat = function isMiddleEndianFormat(dateString) {
  return isMatch(dateString, 'MM/dd/yyyy');
};

/**
 * checks to see if string is in Big Endian format (YYYY/MM/DD)
 * @param {string} dateString
 * @returns {boolean}
 */
var isBigEndianFormat = function isBigEndianFormat(dateString) {
  return isMatch(dateString, 'yyyy/MM/dd');
};

/**
 * checks to see if string is in ISO format (YYYY-MM-DD)
 * @param {string} dateString
 * @returns {boolean}
 */
var isIsoFormat = function isIsoFormat(dateString) {
  return parseJSON(dateString).toString() !== 'Invalid Date';
};

/**
 * Ensures a format string is in the correct format for date-fns
 * @param {string} formatString
 * @returns {string}
 */
var ensureFormat = function ensureFormat(formatString) {
  return formatString === null || formatString === void 0 ? void 0 : formatString.replace(/Y/g, 'y').replace(/D/g, 'd');
};

/**
 * Ensure a string or a Date is a one of the supported formats
 * @param {string|Date} date
 * @returns {Date}
 */
function ensureDate(date) {
  if (date instanceof Date && isValid(date)) {
    return date;
  }
  if (isBigEndianFormat(date)) {
    return parse(date, 'yyyy/MM/dd', new Date());
  }
  if (isMiddleEndianFormat(date)) {
    return parse(date, 'MM/dd/yyyy', new Date());
  }
  if (isFiscalDateFormat(date)) {
    return parse(date, 'yyyyMMdd', new Date());
  }
  if (isIsoFormat(date)) {
    return parseJSON(date);
  }
  return undefined;
}

/**
 * External
 * @param {string | Date} date
 * @param {string} [formatString = MIDDLE_ENDIAN_FORMAT]
 * @returns {string}
 */
function formatDate(date) {
  var formatString = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : MIDDLE_ENDIAN_FORMAT;
  var dateObj = ensureDate(date);
  return dateObj ? format(dateObj, ensureFormat(formatString)) : '';
}

/**
 * Extracts the month index from a date string
 * @param activeMonth
 * @returns {number|string}
 */
function getMonthOfDate(activeMonth) {
  var date = ensureDate(activeMonth);
  return date ? getMonth(date) : '';
}

/**
 * Extracts the month name from a date string
 * @param {string} activeMonth
 * @returns {string}
 */
function getMonthNameOfDate(activeMonth) {
  var date = ensureDate(activeMonth);
  return date ? format(date, 'MMMM') : '';
}

/**
 *  Extracts the year from a date string
 * @param {string} activeMonth
 * @returns {number|string}
 */
function getYearOfDate(activeMonth) {
  var date = ensureDate(activeMonth);
  return activeMonth ? getYear(date) : '';
}

/**
 * Checks if a year is within the bounds of the range start and end years
 * @param {Object} yearRange
 * @param {string} currentYear
 * @returns {string}
 */
function checkYearInRange(yearRange, currentYear) {
  if (currentYear < (yearRange === null || yearRange === void 0 ? void 0 : yearRange.startYear) || currentYear > (yearRange === null || yearRange === void 0 ? void 0 : yearRange.endYear)) {
    return yearRange.startYear; // return start year
  }
  return currentYear; // if no yearRange return current year
}
function checkSelectRangeInYearRange(yearRange, date) {
  var year = getYear(date);
  if (year < (yearRange === null || yearRange === void 0 ? void 0 : yearRange.startYear) || year > (yearRange === null || yearRange === void 0 ? void 0 : yearRange.endYear)) {
    return false; // return false if year is outside of yearRange
  }
  return true; // return true if year is within of yearRange
}
function capitalizeFirstLetter(string) {
  string = string.toLowerCase();
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Checks if a date is within the bounds of the range start and end dates
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} currentDate
 * @returns {boolean}
 */
function checkDateInRange(startDate, endDate, currentDate) {
  var start = new Date(startDate);
  var end = new Date(endDate);
  var date = new Date(currentDate);
  return date >= start && date <= end; // true or false
}

/**
 * Checks if the end date is past specified day range
 * @param {Date|string} startDate
 * @param {Date|string} endDate
 * @param {number} numberOfDays
 * @returns {boolean}
 */
function checkDatePastRange(startDate, endDate, numberOfDays) {
  var startDateObj = ensureDate(startDate);
  var endDateObj = ensureDate(endDate);
  return isValid(startDateObj) && isValid(endDateObj) && endDateObj > addDays(startDateObj, numberOfDays);
}
function chunk(arr, size) {
  return Array.from({
    length: Math.ceil(arr.length / size)
  }, function (v, i) {
    return arr.slice(i * size, i * size + size);
  });
}

/**
 * Checks if the current month is at the start or end of the year range
 * @param {object|undefined} yearRange
 * @param {object} activeMonth
 * @param {object} isMonthNavDisabled
 * @returns {object|null}
 */
function shouldMonthNavDisable(yearRange, activeMonth, isMonthNavDisabled) {
  var currentYear = getYearOfDate(activeMonth);
  var currentMonthName = getMonthNameOfDate(activeMonth);
  if (yearRange.startYear === currentYear && currentMonthName === 'January') {
    return {
      prev: true
    };
  } else if (yearRange.startYear !== currentYear && isMonthNavDisabled.prev || currentMonthName !== 'January' && isMonthNavDisabled.prev) {
    return {
      prev: false
    };
  } else if (yearRange.endYear === currentYear && currentMonthName === 'December') {
    return {
      next: true
    };
  } else if (yearRange.endYear !== currentYear && isMonthNavDisabled.next || currentMonthName !== 'December' && isMonthNavDisabled.next) {
    return {
      next: false
    };
  } else {
    return null;
  }
}

export { capitalizeFirstLetter, checkDateInRange, checkDatePastRange, checkSelectRangeInYearRange, checkYearInRange, chunk, ensureDate, formatDate, getMonthNameOfDate, getMonthOfDate, getTodayDate, getYearOfDate, shouldMonthNavDisable };
